import React from 'react';
import Swiper from 'swiper';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { OndeAtuaCard } from './OndeAtuaCard';
import type {
  OndeAtuaCard as OndeAtuaCardModel,
  YoutubeVideoRef,
} from './helpers';

interface OndeAtuaCarouselProps {
  cards: OndeAtuaCardModel[];
  isEditMode?: boolean;
  onPlay?: (video: YoutubeVideoRef) => void;
  onEditItem?: (index: number) => void;
}

/** Quantos cards cabem na largura máxima da coluna. */
const MAX_SLIDES_PER_VIEW = 2.5;

/**
 * As setas flutuam sobre as fotos, e não no centro vertical do carrossel: o
 * card tem legenda abaixo da imagem, então o meio da foto fica em torno de 35%
 * da altura total. O estado desabilitado vem do `.swiper-button-disabled` que
 * o Swiper aplica (não do atributo `disabled`) — ver `_capa-onde-atua.scss`.
 */
const ARROW_CLASS =
  'absolute top-[35%] z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg bg-black/40 text-white transition-colors hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white';

/**
 * Carrossel de cards de um tema.
 *
 * Swiper na API imperativa: guarda
 * de SSR, trava contra o duplo-monte do StrictMode e `querySelector` escopado
 * ao próprio nó (nunca `document`, senão dois carrosséis na mesma página
 * disputariam os mesmos controles).
 *
 * Sem autoplay: a faixa é uma prateleira de conteúdo, não um banner, e um
 * carrossel que anda sozinho ao lado de abas atrapalha quem está lendo.
 *
 * Quem troca de aba remonta este componente inteiro (`key` no pai), o que
 * reinicializa o Swiper com o novo conjunto de slides — mais simples e mais
 * seguro do que atualizar a instância em cima de uma lista trocada.
 */
export const OndeAtuaCarousel: React.FC<OndeAtuaCarouselProps> = ({
  cards,
  isEditMode,
  onPlay,
  onEditItem,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const swiperRef = React.useRef<Swiper | null>(null);
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (initialized.current) return undefined;
    const el = rootRef.current?.querySelector<HTMLElement>('.mpf-swiper-atua');
    if (!el) return undefined;

    initialized.current = true;
    swiperRef.current = new Swiper(el, {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 1,
      spaceBetween: 16,
      // SEM `loop`, e é correção de acessibilidade (AB#11509, AB#9144).
      //
      // Com `loop` o Swiper reordena os slides no DOM a cada troca. Quem
      // navega por teclado foca um card, o módulo de a11y traz esse slide
      // para a frente, o DOM é reordenado e o próximo Tab cai no card
      // seguinte do mesmo carrossel — para sempre: medido em produção, 70
      // Tabs sem sair da faixa. O `rewind` dá às setas o mesmo "volta ao
      // começo" sem mexer no DOM.
      loop: false,
      rewind: true,
      speed: 500,
      // Sem as mensagens, o módulo de a11y sobrescreve o `aria-label` dos
      // nossos botões com os textos padrão dele, em inglês.
      a11y: {
        enabled: true,
        prevSlideMessage: 'Cards anteriores',
        nextSlideMessage: 'Próximos cards',
        firstSlideMessage: 'Este é o primeiro card',
        lastSlideMessage: 'Este é o último card',
        paginationBulletMessage: 'Ir para o card {{index}}',
      },
      resizeObserver: false,
      breakpoints: {
        640: { slidesPerView: 2, spaceBetween: 16 },
        1024: { slidesPerView: MAX_SLIDES_PER_VIEW, spaceBetween: 16 },
      },
      navigation: {
        prevEl: el.querySelector<HTMLElement>('.mpf-swiper-atua-prev'),
        nextEl: el.querySelector<HTMLElement>('.mpf-swiper-atua-next'),
      },
      pagination: {
        el: el.querySelector<HTMLElement>('.mpf-swiper-atua-pagination'),
        clickable: true,
      },
    });

    return () => {
      swiperRef.current?.destroy(true, true);
      swiperRef.current = null;
      initialized.current = false;
    };
  }, [cards.length]);

  if (cards.length === 0) return null;

  return (
    <div ref={rootRef}>
      <div className="swiper mpf-swiper-atua relative">
        <div className="swiper-wrapper">
          {cards.map((card) => (
            /* `aspect` reservado no slide: sem altura declarada, o carrossel
               nasce achatado e cresce quando as fotos chegam. */
            <div
              key={card.key}
              className="swiper-slide h-auto"
              suppressHydrationWarning
            >
              <OndeAtuaCard
                card={card}
                isEditMode={isEditMode}
                onPlay={onPlay}
                onEditItem={onEditItem}
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          aria-label="Cards anteriores"
          data-tooltip="Cards anteriores"
          className={`mpf-swiper-atua-prev left-2 ${ARROW_CLASS}`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="h-5 w-5"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Próximos cards"
          data-tooltip="Próximos cards"
          className={`mpf-swiper-atua-next right-2 ${ARROW_CLASS}`}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="h-5 w-5"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>

        <div className="mpf-swiper-atua-pagination mt-4 flex justify-center gap-2" />
      </div>
    </div>
  );
};

export default OndeAtuaCarousel;
